/**
 * Passport Photo API
 * Endpoints:
 *  - POST /api/passport-photo/suggest-crop
 *  - POST /api/passport-photo
 */
import apiClient, { createAuthConfig, isMockApi } from "../client";
import { appendLineageToForm } from "./studio_features";
import { appendAiImage } from "./aiUpload";

const MULTIPART = (token) => ({
  ...createAuthConfig(token),
  timeout: 300000,
  headers: {
    ...createAuthConfig(token).headers,
    "Content-Type": "multipart/form-data",
  },
});

/**
 * Suggest a crop region for a passport-sized photo.
 * @param {string} token - Clerk session token
 * @param {File|null} file - Image file (use null when sending image_url)
 * @param {object} opts
 * @param {string} [opts.image_url]
 * @param {number} opts.w_mm - Photo width in mm (required)
 * @param {number} opts.h_mm - Photo height in mm (required)
 * @param {number} [opts.dpi=300]
 * @param {number} [opts.head_h_ratio_min]
 * @param {number} [opts.head_h_ratio_max]
 * @param {number} [opts.eye_line_from_top]
 * @param {string} [opts.quality] - low | medium | high (sets DPI on the backend)
 * @returns {Promise<{success: boolean, result?: object, message?: string}>}
 */
export async function passportSuggestCrop(token, file, opts = {}) {
  if (opts.w_mm == null || opts.h_mm == null) {
    throw new Error("passportSuggestCrop: w_mm and h_mm are required");
  }

  if (isMockApi) {
    return {
      success: true,
      result: { crop_left: 0, crop_top: 0, crop_w: 0, crop_h: 0 },
    };
  }

  const form = new FormData();
  await appendAiImage(form, file, {
    imageUrl: opts.image_url,
    requiredMessage: "passportSuggestCrop: provide file or opts.image_url",
  });
  form.append("w_mm", String(opts.w_mm));
  form.append("h_mm", String(opts.h_mm));
  if (opts.dpi != null) form.append("dpi", String(opts.dpi));
  if (opts.head_h_ratio_min != null) {
    form.append("head_h_ratio_min", String(opts.head_h_ratio_min));
  }
  if (opts.head_h_ratio_max != null) {
    form.append("head_h_ratio_max", String(opts.head_h_ratio_max));
  }
  if (opts.eye_line_from_top != null) {
    form.append("eye_line_from_top", String(opts.eye_line_from_top));
  }
  if (opts.quality) form.append("quality", String(opts.quality));

  const response = await apiClient.post(
    "/api/passport-photo/suggest-crop",
    form,
    MULTIPART(token)
  );
  return response.data;
}

/**
 * Generate a passport-sized photo.
 * @param {string} token - Clerk session token
 * @param {File|null} file - Image file (use null when sending image_url)
 * @param {object} opts
 * @param {string} [opts.image_url]
 * @param {number} opts.w_mm - Photo width in mm (required)
 * @param {number} opts.h_mm - Photo height in mm (required)
 * @param {number} [opts.dpi=300]
 * @param {string} [opts.bg_hex] - e.g. "#FFFFFF"
 * @param {number} [opts.head_h_ratio_min]
 * @param {number} [opts.head_h_ratio_max]
 * @param {number} [opts.eye_line_from_top]
 * @param {string} [opts.output_format='png']
 * @param {boolean} [opts.white_background]
 * @param {boolean} [opts.print_sheet]
 * @param {string} [opts.sheet_size] - e.g. "4x6", "A4"
 * @param {number} [opts.crop_left]
 * @param {number} [opts.crop_top]
 * @param {number} [opts.crop_w]
 * @param {number} [opts.crop_h]
 * @param {string} [opts.quality] - low | medium | high (billed as passport_photo_*)
 * @returns {Promise<{success: boolean, output_url?: string, photo_size?: string, credits_used?: number, remaining_credits?: number, creation_id?: string, message?: string}>}
 */
export async function passportGenerate(token, file, opts = {}) {
  if (opts.w_mm == null || opts.h_mm == null) {
    throw new Error("passportGenerate: w_mm and h_mm are required");
  }

  if (isMockApi) {
    const mockSrc = opts.image_url || (file ? URL.createObjectURL(file) : "");
    return {
      success: true,
      output_url: mockSrc || "",
      photo_size: `${opts.w_mm}x${opts.h_mm}mm`,
      credits_used: 1,
      remaining_credits: 99,
    };
  }

  const form = new FormData();
  await appendAiImage(form, file, {
    imageUrl: opts.image_url,
    requiredMessage: "passportGenerate: provide file or opts.image_url",
  });
  form.append("w_mm", String(opts.w_mm));
  form.append("h_mm", String(opts.h_mm));
  if (opts.dpi != null) form.append("dpi", String(opts.dpi));
  if (opts.bg_hex) form.append("bg_hex", opts.bg_hex);
  if (opts.head_h_ratio_min != null) {
    form.append("head_h_ratio_min", String(opts.head_h_ratio_min));
  }
  if (opts.head_h_ratio_max != null) {
    form.append("head_h_ratio_max", String(opts.head_h_ratio_max));
  }
  if (opts.eye_line_from_top != null) {
    form.append("eye_line_from_top", String(opts.eye_line_from_top));
  }
  if (opts.output_format) form.append("output_format", opts.output_format);
  if (opts.white_background != null) {
    form.append("white_background", String(opts.white_background));
  }
  if (opts.print_sheet != null) {
    form.append("print_sheet", String(opts.print_sheet));
  }
  if (opts.sheet_size) form.append("sheet_size", opts.sheet_size);
  if (opts.crop_left != null) form.append("crop_left", String(opts.crop_left));
  if (opts.crop_top != null) form.append("crop_top", String(opts.crop_top));
  if (opts.crop_w != null) form.append("crop_w", String(opts.crop_w));
  if (opts.crop_h != null) form.append("crop_h", String(opts.crop_h));
  if (opts.quality) form.append("quality", String(opts.quality));
  appendLineageToForm(form, opts.lineage);

  const response = await apiClient.post(
    "/api/passport-photo",
    form,
    MULTIPART(token)
  );
  return response.data;
}

export default {
  passportSuggestCrop,
  passportGenerate,
};
